import Config from 'config/settings';
import { globals } from 'config/globals';
import { HomeParticle } from 'app/views/home/webgl/particles/fields/HomeParticle';
import ParticlePool from 'app/views/home/webgl/particles/fields/ParticlePool';
import ParticleGridTargets from 'app/views/home/webgl/particles/targets/ParticleGridTargets';
import ParticleGridPatterns from 'app/views/home/webgl/particles/targets/ParticleGridPatterns';
import { Vector2 } from 'app/contracts/Geometry';
import { RepellType } from 'app/enum/Particles';
import ComboCounter from 'utils/ComboCounter';
import HomeScreens from 'app/views/home/webgl/particles/scenes/HomeScreens';
import { device } from 'utils/device';

export default class {
	private startTargetId = null;
	private endTargetId = null;
	private pool = 'systems';
	private totalParticleCount = 0;
	private totalParticleLimit = 100;
	private particlesPerSpawn = 2;
	private spawnFrames = 5;
	private counter = 0;
	private active: boolean = false;
	private _endTargetGridPoint: { col: number; row: number } = { col: 0, row: 0 };
	private headParticle: HomeParticle;
	private _fastMode: boolean = false;

	private test = 1;
	private col = 10;
	private row = 5;
	private _targetPasses = 5;
	private _passesCount = 0;
	private _minPasses = 3;
	private _maxPasses = 15;
	private _counters: ComboCounter = new ComboCounter();
	private _startCounterActive: boolean = false;

	constructor(
		startGridId,
		endGridId,
		totalParticleLimit = 100,
		particlesPerSpawn = 2,
		spawnFrames = 5,
		pool = 'systems',
		delay = 0
	) {
		this.startTargetId = startGridId;
		this.endTargetId = endGridId;
		this.totalParticleCount = 0;
		this.totalParticleLimit = totalParticleLimit;
		this.spawnFrames = spawnFrames;
		this.particlesPerSpawn = particlesPerSpawn;
		this.pool = pool;

		if (!delay) {
			this.next();
			return;
		}

		this.active = true;
		this._counters.clear();
		this._counters.add(this.next.bind(this), delay);
		this._startCounterActive = true;
	}

	public set endTargetGridPoint(targetPosition: { col: number; row: number }) {
		this._endTargetGridPoint = targetPosition;
	}

	public set fastMode(fastMode: boolean) {
	}

	public clear() {
		this._counters.clear();

		if (this.headParticle) {
			this.headParticle.pooled = false;
			this.headParticle.deactivate();
		}
	}

	public tick() {
		if (!this.active) {
			return;
		}

		this._counters.tick();

		if (!this._startCounterActive) {
			this.counter -= (window as any).deltaTime;

			if (this.counter <= 0) {
				this.next();
			}
		}

		this._processSaveZoneHide();
	}

	private _processSaveZoneHide() {
		if (!this.headParticle) {
			return;
		}

		const xOffset = this.headParticle.sceneProps.mouse.particleContainersX['systems'];
		const inSafeZone = ParticleGridTargets.inSafeZone(
			xOffset + this.headParticle.position.absX,
			this.headParticle.position.absY
		);

		this.headParticle.animatedProps.targetAlpha = inSafeZone ? 0.05 : 1;
	}

	public retarget(particle: HomeParticle, newGridPoint: string) {
		let speedVal = this._fastMode ? 100 + Math.random() * 200 : 600 + Math.random() * 600;
		speedVal *= globals.fastModeScale;

		particle.particlePhysics.startLerpToTarget(newGridPoint, speedVal);
	}

	private next() {
		this.active = true;
		this._startCounterActive = false;

		for (let i = 0; i < this.particlesPerSpawn; i++) {
			if (this.totalParticleCount < this.totalParticleLimit) {
				this.spawnParticle();
			} else {
				this.onLineSpawnComplete();
				break;
			}
		}

		this.counter = this.spawnFrames;
	}

	private spawnParticle() {
		this._fastMode = false;
		let particle: HomeParticle = ParticlePool.get(this.pool);

		if (!particle) {
			return;
		}

		this._passesCount = 0;
		this._targetPasses = globals.singleLineMode
			? 1
			: this._minPasses + Math.round(Math.random() * (this._maxPasses - this._minPasses));

		particle.deactivateAllBehaviours(true);
		particle.active = true;
		particle.particlePhysics.active = true;
		particle.particlePhysics.spawnAtTarget(this.startTargetId);

		let speedVal = this._fastMode ? 100 + Math.random() * 200 : 700;
		speedVal *= globals.fastModeScale;
		particle.particlePhysics.startLerpToTarget(this.endTargetId, speedVal);

		particle.particlePhysics.onTargetReached = this.particleTargetReached.bind(this);
		particle.animatedProps.setVectorStyleMovement(false);

		let speed = 0.001 + Math.random() * 0.001;
		speed = 0.005;
		particle.particlePhysics.maxX = speed;
		particle.particlePhysics.maxY = speed;

		particle.emitter.active = true;
		particle.emitter.startDistanceMode(particle.position.x, particle.position.y);
		particle.emitter.randomiseChildLifetime(420, 30);

		particle.animatedProps.setEase({
			alpha: 5
		});

		this.totalParticleCount++;

		this.headParticle = particle;

		let generateSpawnMarker = true;
		if (generateSpawnMarker) {
			this.generateTargetMarker(particle.particlePhysics.lerpTargetPosition);
		}
	}

	public generateTargetMarker(position: Vector2) {
		this.generateMarker(position, 0.1 + Math.random() * 0.45);
	}


	private generateMarker(position: Vector2, scale: number) {
		let particle: HomeParticle = ParticlePool.get(this.pool);

		if (!particle) {
			return;
		}

		particle.deactivateAllBehaviours(true);
		particle.active = true;
		particle.position.set(position, true);
		particle.animatedProps.setVectorStyleMovement(false);

		particle.animatedProps.setEase({
			scale: 20,
			alpha: 20
		});

		const texture = particle.changeParticle(null, true);
		const isSquare = texture == 'solid-square.png';
		if (isSquare && scale > 0.4) {
			scale *= 0.55;
		}

		if (device.innerWidth < Config.mobileBreakpoint) {
			scale *= 1.4;
		}

		particle.animatedProps.scale = 0;
		particle.animatedProps.targetScale = scale;
		particle.animatedProps.alpha = 0;
		particle.animatedProps.targetAlpha = 1;

		//RED:

		//BLUE

		//YELLOW

		//HOT PINK

		const colour = Math.random() < 0.3 ? '#ffffff' : globals.accentColour;
		const railColour = Math.random() < 0.5 ? '#ffffff' : colour;

		this.headParticle.emitter.setColour(railColour);

		particle.colour.set(colour);

		particle.animatedProps.stopBreathing();

		let isMobile = device.innerWidth <= HomeScreens.switchScreensLayoutAt;

		this._counters.add(() => {
			particle.animatedProps.setVectorStyleMovement(true);

			particle.repell.target = particle.sceneProps.targets.systemsMouse;
			//Allow for 150px circle and very tight ring:
			let isMobile = device.innerWidth <= HomeScreens.switchScreensLayoutAt;

			particle.repell.active = true;
			particle.repell.type = RepellType.PUSH;
			particle.repell.radius = 50;
			particle.repell.randomisePower(30, 200);

			if (!isMobile && !isSquare && Math.random() < 0.75) {
				particle.animatedProps.startBreathing(
					scale,
					0.02 * scale,
					0.8 * scale,
					[-1, 1],
					Math.random() * 0.4 * scale,
					Math.random() < 0.5 ? -1 : 1
				);
			}
		}, 1000);

		this._counters.add(
			() => {
				particle.animatedProps.setEase({
					scale: 10 + Math.random() * 40,
					alpha: 40
				});
				particle.animatedProps.targetAlpha = 0;
				particle.animatedProps.targetScale = 0;
				particle.animatedProps.stopBreathing();
			},
			isMobile ? 4000 : 6000
		);

		this._counters.add(
			() => {
				particle.resetParticle();
				particle.pooled = false;
				particle.deactivate();
			},
			isMobile ? 8000 : 10000
		);
	}

	private particleTargetReached(particle, repawn = false) {
		this._passesCount++;


		let x = Math.ceil(Math.random() * 2.99) - 2;
		let y = Math.ceil(Math.random() * 2.99) - 2;

		this.col += x;
		this.row += y;

		if (this.col > ParticleGridTargets.cols - 1) this.col = ParticleGridTargets.cols - 1;
		else if (this.col < 1) this.col = 1;

		if (this.row > ParticleGridTargets.rows - 1) this.row = ParticleGridTargets.rows - 1;
		else if (this.row < 1) this.row = 1;


		let target;
		let respawn = false;

		if (this._passesCount >= this._targetPasses) {
			respawn = true;
			target = ParticleGridPatterns.getSpawnTarget();
		} else {
			if (globals.rightAngleMode) {
				let minSteps =
					device.innerWidth <= HomeScreens.switchScreensLayoutAt ? 3 + Math.random() * 3 : 1;
				target = ParticleGridPatterns.getRightAngleTarget(
					this._endTargetGridPoint.col,
					this._endTargetGridPoint.row,
					minSteps + Math.random() * 1
				);
			} else {
				let minSteps = device.innerWidth <= HomeScreens.switchScreensLayoutAt ? 4 : 1;
				target = ParticleGridPatterns.get45DegreeTarget(
					this._endTargetGridPoint.col,
					this._endTargetGridPoint.row,
					minSteps + Math.random() * 1
				);
			}
		}

		if (target) {
			if (!respawn) {
				this.retarget(particle, 'grid_' + target.col + '_' + target.row);
				this.endTargetGridPoint = target;
			} else {
				let minSteps = globals.singleLineMode ? 1 + Math.random() * 3 : 2 + Math.random() * 7;
				let newGridPoint;
				if (globals.rightAngleMode) {
					newGridPoint = ParticleGridPatterns.getRightAngleTarget(target.col, target.row, minSteps);
				} else {
					newGridPoint = ParticleGridPatterns.get45DegreeTarget(target.col, target.row, minSteps);
				}

				this._fastMode = false;

				let upperLimit = device.innerWidth < Config.mobileBreakpoint ? 1000 : 600;
				let lowerLimit = device.innerWidth < Config.mobileBreakpoint ? 300 : 600;
				let speedVal = this._fastMode ? 100 + Math.random() * 200 : lowerLimit + Math.random() * upperLimit;

				speedVal *= globals.fastModeScale;

				this.headParticle.particlePhysics.spawnAtTarget('grid_' + target.col + '_' + target.row);

				this.headParticle.particlePhysics.startLerpToTarget(
					'grid_' + newGridPoint.col + '_' + newGridPoint.row,
					speedVal
				);
				this.endTargetGridPoint = newGridPoint;

				this._passesCount = 0;
				this._targetPasses = globals.singleLineMode
					? 1
					: this._minPasses + Math.round(Math.random() * (this._maxPasses - this._minPasses));

				this.headParticle.emitter.active = true;
				this.headParticle.emitter.startDistanceMode(this.headParticle.position.x, this.headParticle.position.y);
				this.headParticle.emitter.randomiseChildLifetime(420, 30);
			}

			this.generateTargetMarker(particle.particlePhysics.lerpTargetPosition);
		}

		return;

		if (this.test == 1) {
			this.retarget(particle, 'grid_19_11');
		} else if (this.test == 2) {
			this.retarget(particle, 'grid_19_6');
			this.test = 3;
		} else if (this.test == 3) {
			if (repawn) {
				particle.particlePhysics.spawnAtTarget(
					'grid_' +
						(1 + Math.round(Math.random() * (ParticleGridTargets.cols - 1))) +
						'_' +
						(1 + Math.round(Math.random() * (ParticleGridTargets.rows - 1)))
				);
			}

			this.retarget(
				particle,
				'grid_' +
					(1 + Math.round(Math.random() * (ParticleGridTargets.cols - 1))) +
					'_' +
					(1 + Math.round(Math.random() * (ParticleGridTargets.rows - 1)))
			);
		}

		this.retarget(
			particle,
			'grid_' +
				(1 + Math.round(Math.random() * (ParticleGridTargets.cols - 1))) +
				'_' +
				(1 + Math.round(Math.random() * (ParticleGridTargets.rows - 1)))
		);

		return;

		ParticlePool.free(particle);
	}

	private onLineSpawnComplete() {}
}
